var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { Button, Loading, MessageBanner, Modal, ModalFooter, TextArea, TextField, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { AgendaChangeModal } from "./AgendaChangeModal";
import { AgendaHandlerChoice } from "./AgendaHandlerChoice";
import { linkIssueToMonograph } from "../../api/agendaService";
import { ErrorType } from "../../types/ErrorType";
import { formatIsbn, isCompleteIsbn } from "../../utils/isbn";
import { readErrorCode } from "../../utils/errorHandler";
import { useServerErrorMessage } from "../../hooks/useServerErrorMessage";
import { ProvisionalTitleModal } from "../title/ProvisionalTitleModal";
import css from "./Agenda.module.css";
var LOOKUP_ERROR_ID = "ui-br-legaldeposits.agenda.markMonographic.lookupFailed";
export var MarkMonographicIssueWizard = function (_a) {
    var open = _a.open, issueId = _a.issueId, issueLabel = _a.issueLabel, onClose = _a.onClose;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var _b = useState("monograph"), step = _b[0], setStep = _b[1];
    var _c = useState(""), isbn = _c[0], setIsbn = _c[1];
    var _d = useState(false), isbnTouched = _d[0], setIsbnTouched = _d[1];
    var _e = useState(null), monograph = _e[0], setMonograph = _e[1];
    var _f = useState(null), unknownIsbn = _f[0], setUnknownIsbn = _f[1];
    var _g = useState(null), provisionalMode = _g[0], setProvisionalMode = _g[1];
    var _h = useState(null), handler = _h[0], setHandler = _h[1];
    var _j = useState(""), note = _j[0], setNote = _j[1];
    var currentIsbn = useRef("");
    currentIsbn.current = isbn.trim();
    var lookup = useMutation(
    // a dry-run link without a handler is the lookup: it resolves the ISBN exactly as the real link will
    function (value) {
        return linkIssueToMonograph(kyInstance, issueId, { isbn: value }, true);
    }, {
        onSuccess: function (result, value) {
            // a lookup still in flight when the ISBN was edited must not select the earlier monograph
            if (value !== currentIsbn.current)
                return;
            var link = result.specialIssueLink;
            if (link) {
                setMonograph({
                    titleId: link.monographTitleId,
                    name: link.monographTitle,
                    isbn: link.monographIsbn,
                });
            }
        },
        onError: function (error, value) { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (value !== currentIsbn.current)
                            return [2 /*return*/];
                        return [4 /*yield*/, readErrorCode(error)];
                    case 1:
                        if ((_a.sent()) === ErrorType.MONOGRAPH_ISBN_UNKNOWN) {
                            setUnknownIsbn(value);
                        }
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var resetLookup = lookup.reset;
    useEffect(function () {
        if (!open) {
            setStep("monograph");
            setIsbn("");
            setIsbnTouched(false);
            setMonograph(null);
            setUnknownIsbn(null);
            setProvisionalMode(null);
            setHandler(null);
            setNote("");
            resetLookup();
        }
    }, [open, resetLookup]);
    var lookupError = useServerErrorMessage(unknownIsbn ? null : lookup.error, LOOKUP_ERROR_ID);
    var trimmedIsbn = isbn.trim();
    var isbnInvalid = isbnTouched && trimmedIsbn.length > 0 && !isCompleteIsbn(trimmedIsbn);
    var noteValue = note.trim() || undefined;
    var handleIsbnChange = useCallback(function (e) {
        setIsbn(e.target.value);
        setMonograph(null);
        setUnknownIsbn(null);
        resetLookup();
    }, [resetLookup]);
    var handleIsbnBlur = useCallback(function () { return setIsbnTouched(true); }, []);
    var runLookup = lookup.mutate;
    var handleLookup = useCallback(function () {
        setIsbnTouched(true);
        if (isCompleteIsbn(trimmedIsbn)) {
            runLookup(trimmedIsbn);
        }
    }, [runLookup, trimmedIsbn]);
    var handleIsbnKeyDown = useCallback(function (e) {
        if (e.key === "Enter") {
            e.preventDefault();
            handleLookup();
        }
    }, [handleLookup]);
    var openProvisionalWithIsbn = useCallback(function () { return setProvisionalMode("withIsbn"); }, []);
    var openProvisionalWithoutIsbn = useCallback(function () { return setProvisionalMode("withoutIsbn"); }, []);
    var closeProvisional = useCallback(function () { return setProvisionalMode(null); }, []);
    var handleProvisionalCreated = useCallback(function (_a) {
        var titleId = _a.titleId;
        if (titleId) {
            setMonograph({
                titleId: titleId,
                isbn: provisionalMode === "withIsbn" ? unknownIsbn : null,
            });
            setUnknownIsbn(null);
        }
    }, [provisionalMode, unknownIsbn]);
    var provisionalInitialValues = useMemo(function () { return ({
        name: "",
        isbn: provisionalMode === "withoutIsbn" ? "" : (unknownIsbn !== null && unknownIsbn !== void 0 ? unknownIsbn : ""),
        note: "",
    }); }, [provisionalMode, unknownIsbn]);
    var goToHandler = useCallback(function () { return setStep("handler"); }, []);
    var backToMonograph = useCallback(function () { return setStep("monograph"); }, []);
    var handleNoteChange = useCallback(function (e) { return setNote(e.target.value); }, []);
    var run = useCallback(function (dryRun) {
        if (!monograph || !handler) {
            return Promise.reject(new Error("A monograph and a handler must be chosen first"));
        }
        return linkIssueToMonograph(kyInstance, issueId, {
            monographTitleId: monograph.titleId,
            agenda: handler,
            note: noteValue,
        }, dryRun);
    }, [kyInstance, issueId, monograph, handler, noteValue]);
    var previewKey = useMemo(function () { var _a; return ["mark-monographic", issueId, (_a = monograph === null || monograph === void 0 ? void 0 : monograph.titleId) !== null && _a !== void 0 ? _a : null, handler]; }, [issueId, monograph, handler]);
    var monographName = (monograph === null || monograph === void 0 ? void 0 : monograph.name)
        ? monograph.name
        : formatMessage({
            id: "ui-br-legaldeposits.agenda.markMonographic.provisional",
        });
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { open: open && step === "monograph" && !provisionalMode, label: formatMessage({ id: "ui-br-legaldeposits.agenda.markMonographic.heading" }, { issue: issueLabel }), onClose: onClose, dismissible: true, size: "medium", footer: React.createElement(ModalFooter, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: goToHandler, disabled: !monograph, "data-test-id": "mark-monographic-next" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.wizard.next" })),
                React.createElement(Button, { onClick: onClose },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.cancel" }))) },
            React.createElement("div", { "data-test-id": "mark-monographic-wizard" },
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.markMonographic.intro" })),
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.markMonographic.isbn.label",
                    }), value: isbn, onChange: handleIsbnChange, onBlur: handleIsbnBlur, onKeyDown: handleIsbnKeyDown, error: isbnInvalid
                        ? formatMessage({
                            id: "ui-br-legaldeposits.agenda.issue.isbn.invalid",
                        })
                        : undefined, autoFocus: true, "data-test-id": "mark-monographic-isbn" }),
                React.createElement("div", { className: css.fieldActions },
                    React.createElement(Button, { onClick: handleLookup, disabled: !isCompleteIsbn(trimmedIsbn) || lookup.isLoading, marginBottom0: true, "data-test-id": "mark-monographic-lookup" },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.markMonographic.lookup" })),
                    React.createElement(Button, { onClick: openProvisionalWithoutIsbn, marginBottom0: true, "data-test-id": "mark-monographic-without-isbn" },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.noIsbn.create" }))),
                lookup.isLoading && React.createElement(Loading, null),
                lookupError && (React.createElement(MessageBanner, { type: "error" }, lookupError)),
                unknownIsbn && (React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.unknownIsbn.message", values: { isbn: formatIsbn(unknownIsbn) } }),
                    React.createElement("div", { className: css.actions },
                        React.createElement(Button, { onClick: openProvisionalWithIsbn, marginBottom0: true, "data-test-id": "mark-monographic-provisional" },
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.unknownIsbn.create" }))))),
                monograph && (React.createElement(MessageBanner, { type: "success", "data-test-id": "mark-monographic-found" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.markMonographic.found", values: { name: monographName } }),
                    monograph.isbn && " (".concat(formatIsbn(monograph.isbn), ")"))))),
        React.createElement(AgendaChangeModal, { open: open && step === "handler", heading: formatMessage({ id: "ui-br-legaldeposits.agenda.markMonographic.handler.heading" }, { issue: issueLabel }), previewKey: previewKey, canSubmit: !!monograph && handler !== null, run: run, onClose: onClose, onChanged: onClose, impactScope: "linkedPair" },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.markMonographic.handler.intro", values: { name: monographName } })),
            React.createElement(AgendaHandlerChoice, { name: "mark-monographic-handler", side: "periodicals", value: handler, onChange: setHandler }),
            React.createElement(TextArea, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.dialog.note.label",
                }), value: note, onChange: handleNoteChange }),
            React.createElement("div", { className: css.fieldActions },
                React.createElement(Button, { onClick: backToMonograph, marginBottom0: true },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.wizard.back" })))),
        open && provisionalMode && (React.createElement(ProvisionalTitleModal, { open: true, onClose: closeProvisional, onCreated: handleProvisionalCreated, titleType: "MONOGRAPH", initialValues: provisionalInitialValues, showNotifyAgency: provisionalMode === "withIsbn", disableIsbn: provisionalMode === "withoutIsbn" }))));
};
